import { useEffect, useMemo, useState, useCallback } from "react";
import { Scale, Minus, Square, X, Search, Plus, LayoutDashboard, FolderOpen, CalendarDays, Upload, LogOut, User2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import CaseTree from "@/components/CaseTree";
import CaseDetail from "@/components/CaseDetail";
import NewCaseDialog from "@/components/NewCaseDialog";
import DashboardView from "@/components/DashboardView";
import HearingsPage from "@/pages/HearingsPage";
import BulkImportDialog from "@/components/BulkImportDialog";
import { listCases, deleteCase } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function UyapWorkspace() {
  const { user, logout } = useAuth();
  const [cases, setCases] = useState([]);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("dashboard"); // dashboard | case | calendar
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await listCases();
      setCases(data);
      if (selected) {
        const updated = data.find((c) => c.id === selected.id);
        if (updated) setSelected(updated);
      }
    } catch (e) {
      toast.error("Davalar yüklenemedi");
    } finally { setLoading(false); }
  }, [selected]);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return cases;
    const q = search.toLowerCase();
    return cases.filter(
      (c) =>
        (c.dava_no || "").toLowerCase().includes(q) ||
        (c.mahkeme_adi || "").toLowerCase().includes(q) ||
        (c.muvekkil || "").toLowerCase().includes(q)
    );
  }, [cases, search]);

  const handleSelect = (c) => { setSelected(c); setView("case"); };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu davayı silmek istediğinize emin misiniz? Tüm belgeler, tahsilatlar ve duruşmalar silinecek.")) return;
    try {
      await deleteCase(id);
      toast.success("Dava silindi");
      if (selected?.id === id) { setSelected(null); setView("dashboard"); }
      refresh();
    } catch { toast.error("Silme işlemi başarısız"); }
  };

  return (
    <div className="h-screen flex flex-col" style={{ background: "var(--uyap-bg)" }}>
      {/* Windows-style title bar */}
      <div className="win-titlebar flex items-center justify-between px-4 h-10 shrink-0 relative z-10" data-testid="window-titlebar">
        <div className="flex items-center gap-3">
          <Scale className="w-4 h-4 text-amber-500" />
          <span className="font-serif text-sm font-semibold text-amber-500 tracking-wide">UYAP</span>
          <span className="text-xs text-slate-400 font-mono">AVUKAT PORTAL — Dava & Finans Yönetim Sistemi</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-400 font-mono" data-testid="current-user">
            <User2 className="w-3 h-3" />
            {user?.username || user?.name}
          </div>
          <button
            onClick={logout}
            className="text-slate-400 hover:text-red-400 flex items-center gap-1 text-xs"
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Çıkış
          </button>
          <div className="w-px h-4 bg-slate-700 mx-1" />
          <button className="w-8 h-6 flex items-center justify-center hover:bg-slate-700/50 rounded"><Minus className="w-3 h-3 text-slate-500" /></button>
          <button className="w-8 h-6 flex items-center justify-center hover:bg-slate-700/50 rounded"><Square className="w-3 h-3 text-slate-500" /></button>
          <button className="w-8 h-6 flex items-center justify-center hover:bg-red-600/70 rounded"><X className="w-3 h-3 text-slate-500" /></button>
        </div>
      </div>

      {/* Secondary toolbar */}
      <div className="h-14 shrink-0 border-b border-slate-800 bg-slate-900/60 backdrop-blur flex items-center px-4 gap-2 relative z-10">
        <Button
          variant={view === "dashboard" ? "default" : "ghost"} size="sm"
          onClick={() => setView("dashboard")}
          className={view === "dashboard" ? "bg-amber-600 hover:bg-amber-700 text-slate-950" : "text-slate-300"}
          data-testid="nav-dashboard-btn"
        >
          <LayoutDashboard className="w-4 h-4 mr-2" /> Panel
        </Button>
        <Button
          variant={view === "calendar" ? "default" : "ghost"} size="sm"
          onClick={() => setView("calendar")}
          className={view === "calendar" ? "bg-amber-600 hover:bg-amber-700 text-slate-950" : "text-slate-300"}
          data-testid="nav-calendar-btn"
        >
          <CalendarDays className="w-4 h-4 mr-2" /> Takvim
        </Button>
        <Button
          variant={view === "case" ? "default" : "ghost"} size="sm"
          onClick={() => selected && setView("case")} disabled={!selected}
          className={view === "case" ? "bg-amber-600 hover:bg-amber-700 text-slate-950" : "text-slate-300"}
          data-testid="nav-case-btn"
        >
          <FolderOpen className="w-4 h-4 mr-2" /> {selected ? selected.dava_no : "Dava Seçili Değil"}
        </Button>

        <div className="flex-1" />

        <div className="relative w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Dosya no, mahkeme, müvekkil ara..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-slate-800/80 border-slate-700 text-slate-100 placeholder:text-slate-500 h-9"
            data-testid="global-search-input"
          />
        </div>

        <Button
          onClick={() => setImportOpen(true)} size="sm" variant="outline"
          className="border-slate-700 text-slate-200 hover:bg-slate-800"
          data-testid="bulk-import-btn"
        >
          <Upload className="w-4 h-4 mr-1" /> Toplu Aktarım
        </Button>

        <Button
          onClick={() => setNewOpen(true)} size="sm"
          className="bg-amber-600 hover:bg-amber-700 text-slate-950 font-semibold"
          data-testid="new-case-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Yeni Dava
        </Button>
      </div>

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden relative z-10">
        <aside className="w-[300px] shrink-0 border-r border-slate-800 bg-slate-900/40 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-slate-800">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Ağaç Sınıflandırma</div>
            <div className="text-sm text-slate-200 mt-1">Dosya Numaraları</div>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            <CaseTree cases={filtered} loading={loading} selectedId={selected?.id}
              onSelect={handleSelect} onDelete={handleDelete} />
          </div>
          <div className="px-4 py-2 border-t border-slate-800 text-xs text-slate-500 font-mono">
            {filtered.length} / {cases.length} dava
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto">
          {view === "dashboard" && (
            <DashboardView cases={cases} onSelectCase={handleSelect} onNewCase={() => setNewOpen(true)} />
          )}
          {view === "calendar" && <HearingsPage />}
          {view === "case" && selected && (
            <CaseDetail caseData={selected} onRefresh={refresh} onDelete={() => handleDelete(selected.id)} />
          )}
          {view === "case" && !selected && (
            <div className="h-full flex items-center justify-center text-slate-500">Soldan bir dava seçin</div>
          )}
        </main>
      </div>

      <NewCaseDialog
        open={newOpen} onOpenChange={setNewOpen}
        onCreated={(c) => { refresh(); setSelected(c); setView("case"); }}
      />
      <BulkImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={refresh} />
    </div>
  );
}
